import { DisplayRef, Namespace, Ref, SequencerEditorConfig } from '@repo/sdk/models/components';

import { extractConditionExpressionRefs } from '@/features/equipment-browser/conditional-evaluator/extract-refs';

const collectSequencerEditorRefs = (
  sequencerEditorConfig: SequencerEditorConfig | undefined
): Ref[] => {
  if (!sequencerEditorConfig) {
    return [];
  }

  const refs: Ref[] = [];

  if (sequencerEditorConfig.currentStageRef) {
    refs.push(sequencerEditorConfig.currentStageRef);
  }

  if (sequencerEditorConfig.activeSequenceRef) {
    refs.push(sequencerEditorConfig.activeSequenceRef);
  }

  if (sequencerEditorConfig.cosmicFallbackStageRef) {
    refs.push(sequencerEditorConfig.cosmicFallbackStageRef);
  }

  if (sequencerEditorConfig.cosmicConditionExpression) {
    refs.push(...extractConditionExpressionRefs(sequencerEditorConfig.cosmicConditionExpression));
  }

  if (sequencerEditorConfig.sequences) {
    sequencerEditorConfig.sequences.forEach((sequenceRef) => {
      if (sequenceRef.displayRef?.ref) {
        refs.push(sequenceRef.displayRef.ref);
      }
    });
  }

  if (sequencerEditorConfig.energyAiCompressors) {
    sequencerEditorConfig.energyAiCompressors.forEach((energyAICompressor) => {
      const displayRefs: (DisplayRef | undefined)[] = [
        energyAICompressor.rampDownTimeSecs,
        energyAICompressor.rampDownTimerSecs,
        energyAICompressor.rampUpTimeSecs,
        energyAICompressor.runTimeSecs,
      ];

      displayRefs.forEach((displayRef) => {
        if (displayRef?.ref) {
          refs.push(displayRef.ref);
        }
      });
    });
  }

  return refs;
};

/**
 * Extracts all reading references from a sequencer editor configuration.
 *
 * @param sequencerEditorConfig - The sequencer editor configuration to extract references from
 * @returns The reading references used in the sequencer editor
 */
export const extractReadingsRefsFromSequencerEditorConfig = (
  sequencerEditorConfig: SequencerEditorConfig | undefined
): Ref[] => {
  const refs = collectSequencerEditorRefs(sequencerEditorConfig);

  // Filter to only reading refs (non-Setting namespace), ensure they have valid IDs, and deduplicate
  return [
    ...new Set(
      refs.filter(
        (ref): ref is Ref =>
          ref !== undefined && ref.id !== undefined && ref.namespace !== Namespace.Setting
      )
    ),
  ];
};

/**
 * Extracts all setting references from a sequencer editor configuration.
 *
 * @param sequencerEditorConfig - The sequencer editor configuration to extract references from
 * @returns The setting references used in the sequencer editor
 */
export const extractSettingsRefsFromSequencerEditorConfig = (
  sequencerEditorConfig: SequencerEditorConfig | undefined
): Ref[] => {
  const refs = collectSequencerEditorRefs(sequencerEditorConfig);

  // Filter to only setting refs (Setting namespace), ensure they have valid IDs, and deduplicate
  return [
    ...new Set(
      refs.filter(
        (ref): ref is Ref =>
          ref !== undefined && ref.id !== undefined && ref.namespace === Namespace.Setting
      )
    ),
  ];
};

/**
 * Extracts setting references from the sibling sequences in a sequencer editor configuration.
 * These refs are used to subscribe to sibling sequencer data for swing compressor lock detection.
 */
export const extractSiblingSettingsRefsFromSequencerEditorConfig = (
  sequencerEditorConfig: SequencerEditorConfig | undefined
): Ref[] => {
  if (!sequencerEditorConfig?.siblingSequences) {
    return [];
  }

  return sequencerEditorConfig.siblingSequences
    .filter((seq) => seq.displayRef?.ref?.id !== undefined)
    .map((seq) => seq.displayRef!.ref!);
};
